import {
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router';
import { RetrievalMethod } from '@/feature/profiles/constants';
import { formDefaultValues, PATIENT_REQUEST_STEPS } from '../constants';
import { PatientCreationState, RequestActionFormsData } from '../types';
import { useGetPatientRequest, useGetPatientById } from '../hooks/patientsAPI';
import { useGetCurrentUserProfile } from '@/feature/user-settings';
import { Gender } from '@/feature/patients/types';

export interface PatientRequestContextType {
  currentStepIndex: number;
  setCurrentStep: (step: number) => void;
  requestState: PatientCreationState;
  setRequestState: React.Dispatch<React.SetStateAction<PatientCreationState>>;
  updateActionFormData: <T extends RetrievalMethod>(
    method: T,
    data: Partial<RequestActionFormsData[T]>,
  ) => void;
  isEditMode: boolean;
  isLoading: boolean;
  patientId?: string;
  patientRequestId?: string;
}

export const PatientRequestContext = createContext<
  PatientRequestContextType | undefined
>(undefined);

interface PatientRequestProviderProps {
  children: React.ReactNode;
  patientId?: string;
  patientRequestId?: string;
  initialStep?: number;
  syncUrl?: boolean;
}

export const PatientRequestProvider = ({
  children,
  patientId,
  patientRequestId,
  initialStep = 0,
  syncUrl = false,
}: PatientRequestProviderProps) => {
  // Determine mode based on patientRequestId and patientId
  const isEditMode = !!patientRequestId;
  const isExistingPatientMode = !!patientId && !isEditMode;

  const [searchParams, setSearchParams] = useSearchParams();

  const getValidatedStepIndex = useCallback(
    (step: number) => {
      const minStep = isEditMode ? 1 : 0;
      if (!Number.isFinite(step)) return minStep;
      if (step < minStep) return minStep;
      if (step >= PATIENT_REQUEST_STEPS.length) {
        return PATIENT_REQUEST_STEPS.length - 1;
      }
      return step;
    },
    [isEditMode],
  );

  const stepFromUrlRaw = syncUrl ? searchParams.get('step') : null;
  const stepFromUrl = stepFromUrlRaw ? parseInt(stepFromUrlRaw, 10) : NaN;

  const initialStepIndex = syncUrl
    ? getValidatedStepIndex(
        Number.isFinite(stepFromUrl) ? stepFromUrl : initialStep,
      )
    : initialStep;

  const [currentStep, setCurrentStepState] = useState<number>(initialStepIndex);
  const [requestState, setRequestState] =
    useState<PatientCreationState>(formDefaultValues);

  // Fetch patient data based on mode
  const { data: patientRequestData, isLoading: isLoadingRequest } =
    useGetPatientRequest(patientId, patientRequestId);
  const { data: existingPatientData, isLoading: isLoadingExisting } =
    useGetPatientById(patientId);

  const isLoading = isEditMode
    ? isLoadingRequest || (patientId ? isLoadingExisting : false)
    : isExistingPatientMode
      ? isLoadingExisting
      : isLoadingRequest;

  const { data: currentUser } = useGetCurrentUserProfile();

  const setStepInUrl = useCallback(
    (nextStep: number, opts?: { replace?: boolean }) => {
      if (!syncUrl) return;
      const validatedNext = getValidatedStepIndex(nextStep);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('step', String(validatedNext));
          if (validatedNext !== 1) {
            next.delete('substep');
          }
          return next;
        },
        { replace: opts?.replace ?? false },
      );
    },
    [getValidatedStepIndex, setSearchParams, syncUrl],
  );

  const setCurrentStep = useCallback(
    (step: number) => {
      const validatedNext = getValidatedStepIndex(step);
      setCurrentStepState(validatedNext);
      setStepInUrl(validatedNext);
    },
    [getValidatedStepIndex, setStepInUrl],
  );

  // Ensure URL includes step and keep state in sync with browser back/forward.
  useEffect(() => {
    if (!syncUrl) return;

    const urlStepRaw = searchParams.get('step');
    const urlStepParsed = urlStepRaw ? parseInt(urlStepRaw, 10) : NaN;
    const hasValidUrlStep = Number.isFinite(urlStepParsed);

    if (!hasValidUrlStep) {
      setStepInUrl(currentStep, { replace: true });
      return;
    }

    const validatedUrlStep = getValidatedStepIndex(urlStepParsed);
    if (validatedUrlStep !== urlStepParsed) {
      setStepInUrl(validatedUrlStep, { replace: true });
    }
    if (validatedUrlStep !== currentStep) {
      setCurrentStepState(validatedUrlStep);
    }
  }, [currentStep, getValidatedStepIndex, searchParams, setStepInUrl, syncUrl]);

  // Populate form data when patient data is loaded
  useEffect(() => {
    if (patientRequestData && isEditMode) {
      // Edit mode: merge with defaults to preserve required shape and default submit email.
      setRequestState({
        ...formDefaultValues,
        ...(patientRequestData as PatientCreationState),
        submit: {
          ...formDefaultValues.submit,
          ...(patientRequestData as PatientCreationState).submit,
          notificationEmails: (patientRequestData as PatientCreationState)
            .submit?.notificationEmails?.length
            ? (patientRequestData as PatientCreationState).submit
                .notificationEmails
            : [currentUser?.emailAddress || ''],
        },
      });
    } else if (!isEditMode && !isExistingPatientMode) {
      // New request: only replace submit with email addresses
      setRequestState((prevState) => ({
        ...prevState,
        submit: {
          ...prevState.submit,
          notificationEmails: [currentUser?.emailAddress || ''],
        },
      }));
    }
  }, [
    patientRequestData,
    isEditMode,
    isExistingPatientMode,
    currentUser,
    patientRequestId,
  ]);

  // Populate/refresh patient identity (used by right-side panel header) when we have a patientId.
  // This is required for edit mode because request details don’t include patient demographics.
  useEffect(() => {
    if (!existingPatientData) return;
    if (!isExistingPatientMode && !isEditMode) return;

    setRequestState((prevState) => ({
      ...prevState,
      patient: {
        ...prevState.patient,
        ...existingPatientData,
        genderName: existingPatientData.genderName as Gender,
      },
      organizationProfileId:
        existingPatientData.organizationProfile?.id ||
        prevState.organizationProfileId,
      submit: isExistingPatientMode
        ? {
            ...prevState.submit,
            notificationEmails: [currentUser?.emailAddress || ''],
          }
        : prevState.submit,
    }));
  }, [
    currentUser?.emailAddress,
    existingPatientData,
    isEditMode,
    isExistingPatientMode,
  ]);

  const updateActionFormData = <T extends RetrievalMethod>(
    method: T,
    data: Partial<RequestActionFormsData[T]>,
  ) => {
    setRequestState((prevState) => ({
      ...prevState,
      request: {
        ...prevState.request,
        actionFormsData: {
          ...prevState.request.actionFormsData,
          [method]: {
            ...prevState.request.actionFormsData[method],
            ...data,
          },
        },
      },
    }));
  };

  const value = useMemo(
    () => ({
      currentStepIndex: currentStep,
      setCurrentStep,
      requestState,
      setRequestState,
      updateActionFormData,
      isEditMode,
      isLoading,
      patientId,
      patientRequestId,
    }),
    [
      currentStep,
      requestState,
      isEditMode,
      isLoading,
      patientId,
      patientRequestId,
      setCurrentStep,
    ],
  );

  return (
    <PatientRequestContext.Provider value={value}>
      {children}
    </PatientRequestContext.Provider>
  );
};
